import { adjustments } from "@/components/Buy/BuyEnums";
import { IShoppingCart } from "./api/unifiedPortal/interfaces";
import { logEvent, logException } from "./logger";
import {
  bazaarVoiceCarboniteProductIds,
  bazaarVoiceProductSeatCountIds,
  bazaarVoiceWebrootProductIds,
  webrootProducts,
} from "./webrootProductName";
import { useBuyFlowStore } from "@/stores/buyFlow";
import { getProductDisplayNameBySku } from "./productsBySku";
import { t } from "@/i18n";

export function bazaarVoiceInitialize(scriptUrl: string, brand: string) {
  // Create and configure the script element
  const script = document.createElement("script") as HTMLScriptElement;
  script.async = true;
  script.src = scriptUrl;
  // Append the script element to the document head
  const firstScript = document.getElementsByTagName("script")[0];
  if (firstScript.parentNode != null) firstScript.parentNode.insertBefore(script, firstScript);

  // Set up event handlers for script loading
  script.onload = () => {
    window[`${brand}_BV`] = window.BV;
    logEvent(`${brand} bazaarVoice script loaded`);
    window.BV = undefined;
  };

  script.onerror = () => {
    logEvent(`Failed to load the ${brand} bazaarVoice script`);
  };
}

export function bvCarboniteEventHandler(userShoppingCart: IShoppingCart) {
  const carboniteBV = window.Carbonite_BV as IBV | undefined;
  if (carboniteBV && carboniteBV.pixel && typeof carboniteBV.pixel.trackTransaction === "function") {
    const bvItems: IBVItem[] = [];
    let noTaxTotal = 0;

    userShoppingCart.items.forEach(item => {
      const productId = getBazaarVoiceCarboniteProductId(item);
      bvItems.push({
        price: item.total.toFixed(2),
        quantity: item.quantity,
        productId: productId,
        sku: productId,
        name: t(
          getProductDisplayNameBySku({
            sku: item.sku,
            isTrial: false,
          })
        ),
      });
      noTaxTotal += Number(item.total.toFixed(2));
    });

    const transactionData: IBVTransactionData = {
      currency: "USD",
      orderId: userShoppingCart.orderCode ?? "",
      total: noTaxTotal.toFixed(2),
      items: bvItems,
      encryptedEmail: userShoppingCart.encryptedEmail ?? "",
      encryptionKeyId: userShoppingCart.encryptionKeyId ?? "",
      tax: userShoppingCart?.adjustments.find(a => a.type == adjustments.Tax)?.amount.toString() ?? "0",
    };

    try {
      if (transactionData.items.length > 0) {
        // sends a purchase event to bazaar voice
        carboniteBV.pixel.trackTransaction(transactionData);
      }
    } catch (error) {
      logException(error as Error, `bazaarVoiceEvents`);
    }
    return transactionData;
  }
}

export function bvWebrootEventHandler(userShoppingCart: IShoppingCart) {
  // we have to setup different BV if we load carbonite bazaar voice scripts
  const webrootBV = window.Webroot_BV as IBV | undefined;
  if (webrootBV && webrootBV.pixel && typeof webrootBV.pixel.trackTransaction === "function") {
    const bvItems: IBVItem[] = [];
    let noTaxTotal = 0;

    userShoppingCart.items.forEach(item => {
      if (item.bundledItems && item.bundledItems.length > 0) {
        item.bundledItems.forEach(bundledItem => {
          const bundleProductId = getBazaarVoiceWebrootProductId(bundledItem);
          if (bundleProductId) {
            bvItems.push({
              price: bundledItem.total.toFixed(2),
              quantity: bundledItem.quantity,
              productId: bundleProductId,
              sku: bundleProductId,
              name: bundledItem.sku,
            });
            noTaxTotal += Number(bundledItem.total.toFixed(2));
          }
        });
      }

      const productId = getBazaarVoiceWebrootProductId(item);

      if (productId) {
        bvItems.push({
          price: item.total.toFixed(2),
          quantity: item.quantity,
          productId: productId,
          sku: productId,
          name: t(
            getProductDisplayNameBySku({
              sku: item.sku,
              isTrial: false,
            })
          ),
        });
        noTaxTotal += Number(item.total.toFixed(2));
      }
    });

    const transactionData: IBVTransactionData = {
      currency: "USD",
      orderId: userShoppingCart.orderCode ?? "",
      total: noTaxTotal.toFixed(2),
      items: bvItems,
      encryptedEmail: userShoppingCart.encryptedEmail ?? "",
      encryptionKeyId: userShoppingCart.encryptionKeyId ?? "",
      tax: userShoppingCart?.adjustments.find(a => a.type == adjustments.Tax)?.amount.toString() ?? "0",
    };

    try {
      if (transactionData.items.length > 0) {
        // sends a purchase event to bazaar voice
        webrootBV.pixel.trackTransaction(transactionData);
      }
    } catch (error) {
      logException(error as Error, `bazaarVoiceEvents`);
    }
    return transactionData;
  }
}

export function getBazaarVoiceWebrootProductId(item) {
  const buyFlowStore = useBuyFlowStore();
  const productLicenseCategory = webrootProducts.find(x => x.sku === item.sku)?.licenseCategory ?? "";
  if (bazaarVoiceProductSeatCountIds[productLicenseCategory]) {
    const ratePlan = buyFlowStore.plans
      .find(p => p.sku == item.sku)
      ?.ratePlans.find(r => r.ratePlanId == item.ratePlanId);
    const seats = ratePlan?.includedUnits;
    return bazaarVoiceProductSeatCountIds[productLicenseCategory][seats];
  } else {
    return bazaarVoiceWebrootProductIds[productLicenseCategory];
  }
}

export function getBazaarVoiceCarboniteProductId(item) {
  return bazaarVoiceCarboniteProductIds[item.sku];
}

interface IBVItem {
  price: string;
  quantity: number;
  productId: string; // productId and sku both are same
  sku: string;
  name: string;
  optional_item_parameter?: string; // Optional parameters can be included
}

interface IBVTransactionData {
  currency: string;
  orderId: string;
  total: string;
  items: IBVItem[];
  tax: string;
  optional_order_parameter?: string; // Optional parameters
  optional_PII_parameter?: string; // Optional parameters
  encryptedEmail: string;
  encryptionKeyId: string;
}

interface IBVPixel {
  trackTransaction(data: IBVTransactionData): void;
}

interface IBV {
  pixel: IBVPixel;
}

declare global {
  interface Window {
    BV?: IBV;
    Webroot_BV?: IBV;
    Carbonite_BV?: IBV;
  }
}
